/**
 * DNI Scanner — delegates OCR to the backend (/v1/utils/scan-dni).
 * Requires tesseract-ocr on the server.
 *
 * scanDni(imageSource, side, token, venueToken)
 *   imageSource : File | Blob | data-URL string | HTMLCanvasElement
 *   side        : 'front' | 'back'
 *   token       : JWT bearer token (personal del club, con sesión)
 *   venueToken  : token del enlace de preinscripción del club (formulario público, sin sesión)
 *
 * Returns { success, data, rawText, error }
 *   data: { dni, firstName, lastName, birthDate, expiryDate }
 *         birthDate / expiryDate: { iso: 'YYYY-MM-DD', display: 'DD/MM/YYYY' }
 */

/**
 * Formatos de imagen que Google Cloud Vision NO puede decodificar y que, por
 * tanto, hay que reconvertir a JPEG antes del OCR.
 *
 * Vision lee JPEG/PNG/WEBP/GIF/BMP/TIFF nativamente **y aplica la orientación
 * EXIF él mismo**. Solo HEIC/HEIF (típico de iPhone/iPad) le resulta ilegible.
 *
 * IMPORTANTE (regresión #294): re-encodear TODA imagen por `<canvas>` puede
 * PERDER la orientación EXIF en tablets con navegadores antiguos (el reverso
 * llega girado → la MRZ no se lee → "No se detectó la zona MRZ" en todas las
 * altas) y además baja la resolución de la MRZ. Por eso solo tocamos lo que
 * Vision no puede leer; el resto va INTACTO.
 */
export function needsReencodeForOcr(mimeType) {
  const t = (mimeType || '').toLowerCase();
  return t.includes('heic') || t.includes('heif');
}

/**
 * Re-encode a JPEG SOLO cuando el formato de origen no lo puede leer el OCR
 * (HEIC/HEIF). Para todo lo demás devuelve el blob ORIGINAL sin tocar — así
 * nunca degradamos (ni rotamos, ni reducimos) una imagen que ya funcionaba.
 *
 * Fail-safe: ante cualquier duda o error de decodificación, devuelve el original.
 */
export async function normalizeForOcr(blob) {
  try {
    // JPEG/PNG/WEBP/… → intactos. Vision los lee y auto-rota por EXIF.
    if (!needsReencodeForOcr(blob && blob.type)) return blob;
    if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') {
      return blob;
    }
    let bitmap;
    try {
      bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
      bitmap = await createImageBitmap(blob); // older browsers ignore the options arg
    }
    const { width, height } = bitmap;
    if (!width || !height) return blob;

    // Preservar la resolución de la MRZ: solo capamos un máximo MUY alto por
    // seguridad de memoria; no reducimos el DNI a un tamaño que mate el OCR.
    const MAX_SIDE = 4000;
    const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (typeof bitmap.close === 'function') bitmap.close();

    const jpeg = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.95)
    );
    return jpeg && jpeg.size > 0 ? jpeg : blob;
  } catch {
    return blob;
  }
}

async function toBlob(source) {
  if (source instanceof Blob) return source;
  if (source instanceof HTMLCanvasElement) {
    return new Promise((res, rej) =>
      source.toBlob((b) => (b ? res(b) : rej(new Error('canvas toBlob failed'))), 'image/jpeg', 0.92)
    );
  }
  if (typeof source === 'string' && source.startsWith('data:')) {
    const [header, b64] = source.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const bin  = atob(b64);
    const u8   = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return new Blob([u8], { type: mime });
  }
  throw new Error('Formato de imagen no soportado.');
}

const SCAN_TIMEOUT_MS = 75000;

export async function scanDni(imageSource, side = 'back', token = '', venueToken = '') {
  let blob;
  try {
    blob = await toBlob(imageSource);
  } catch (e) {
    return { success: false, error: 'No se pudo leer el archivo de imagen.', rawText: '' };
  }

  // Normalise to JPEG (fixes HEIC/orientation/oversize) so OCR works across devices.
  blob = await normalizeForOcr(blob);

  let controller;
  let timer;
  try {
    const form = new FormData();
    form.append('file', blob, 'dni.jpg');

    // Con sesión: la ruta del personal. El formulario público de preinscripción no tiene JWT, pero
    // SÍ el token de su club: la ruta con token ata el gasto de OCR (Google Vision, de pago) a un
    // club real y permite cortar por club si alguien abusa. La ruta sin token es LEGACY (el backend
    // la mantiene solo para versiones ya desplegadas) y sólo queda de último recurso.
    if (!token && !venueToken) {
      // Sin sesión y sin token de club solo queda la ruta anónima. Que nadie llegue aquí sin
      // enterarse: cuando el backend la retire, ese llamador empezaría a recibir errores.
      console.warn('scanDni: sin JWT ni token de club — usando la ruta legacy sin token');
    }
    const endpoint = token
      ? `/v1/utils/scan-dni?side=${side}`
      : venueToken
        ? `/v1/pre-register/${encodeURIComponent(venueToken)}/scan-dni?side=${side}`
        : `/v1/pre-register/scan-dni?side=${side}`;

    // Sin tope, un socket muerto dejaba el spinner de Anverso/Reverso girando para siempre.
    // El AbortController cubre también la lectura del cuerpo: el timer se limpia al final.
    controller = new AbortController();
    timer = setTimeout(() => controller.abort(), SCAN_TIMEOUT_MS);
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
      signal: controller.signal,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return {
        success: false,
        error: err.detail || `Error del servidor (${res.status}).`,
        rawText: '',
      };
    }

    return await res.json();
  } catch (err) {
    // Abortado por nuestro timer: la página lo traduce a «rellénalo a mano».
    if (controller?.signal.aborted || err?.name === 'AbortError') {
      return { success: false, error: 'timeout', rawText: '' };
    }
    return { success: false, error: String(err), rawText: '' };
  } finally {
    clearTimeout(timer);
  }
}
